import { Component, OnDestroy } from '@angular/core';
import { ExploreWhatsHappeningService, Tag, SortedSet, TagType, HumanResources, AuthenticationService, AuthenticatedIdentity, Academic } from '../../domain';
import { SavedSearch, Page, Activity, ActivitySpecification, SavedSearchDto, Academic as AcademicOwner } from '../../domain/types/explore-whats-happening';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConsoleService } from '../../widgets/console.service';
import { TooltipService } from '../../widgets/tooltip.service';
import { Project } from '../../domain/types/explore-whats-happening/project';
import { Theme } from '../../domain/types/explore-whats-happening/theme';
import * as XLSX from 'xlsx';

export enum ExploreTypes {
	Activity = "Activities",
	Project = "Projects",
	Theme = "Themes"

}
@Component({
  selector: 'explore-whats-happening',
  templateUrl: './explore-whats-happening.component.html',
  styleUrls: ['./explore-whats-happening.component.scss']
})
export class ExploreWhatsHappeningComponent implements OnDestroy {
	exploreTypes = ExploreTypes;
	introductorySearchPerformed = false;
	searches: SavedSearch[] = [];
	currentPage: Page<Activity> = null;
	currentProjectPage: Page<Project> = null;
	currentThemePage: Page<Theme> = null;
	activities: Activity[] = null;
	projects: Project[] = null;
	themes: Theme[] = null;
	downloadActivities: Activity[] = null;
	downloadProjects: Project[] = null;
	downloadThemes: Theme[] = null;
	spec: ActivitySpecification = null;
	form: FormGroup;
	ExploreTagTypes: TagType[] = [TagType.StrategicInitiative,TagType.Statewide,TagType.ResearchExtension,TagType.UserDefined];
	defaultSearches: [string,ActivitySpecification][] = null;
	meritAndPromotionHR = HumanResources.ANR;
	exploreType: ExploreTypes = ExploreTypes.Activity;
	loading: boolean = false;
	private readonly today: Date = new Date();
  	constructor(
		private readonly svc: ExploreWhatsHappeningService,
		authSvc: AuthenticationService,
		fb: FormBuilder,
		private snackBar: MatSnackBar,
		private console: ConsoleService,
		public tooltipSvc: TooltipService
  	) {
		this.console.log("Explore Whats Happening Component - Initialized");
		this.form = fb.group({
			keyword: '',
			tags: new SortedSet<Tag>(),
			showSeparatedAcademics: false
		});
		svc.usedTags().subscribe(tags => {
			this.form.controls['tags'].setValue(new SortedSet(tags));
			this.defaultSearches = [
				["All", new ActivitySpecification('', [])],
				["Your Tags", new ActivitySpecification('', tags)]
			];
			if(!this.introductorySearchPerformed){
				this.spec = new ActivitySpecification('', tags.slice(0));
				this.svc.explore(this.spec, 1);
				this.svc.exploreProject(this.spec, 1);
				this.svc.exploreTheme(this.spec, 1);
				this.introductorySearchPerformed = true;
			}
		});
		svc.savedSearches().subscribe(searches => {
			this.searches = searches;
		});
		svc.currentActivityPage().subscribe(page => {
			this.currentPage = page;
			
			if(this.activities == null){
				this.activities = [];
			}

			page.items.forEach(item => this.activities.push(item));
		});
		svc.currentProjectPage().subscribe(page => {
			this.currentProjectPage = page;			
			if(this.projects == null){
				this.projects = [];
			}
			page.items.forEach(item => this.projects.push(item));
		});
		svc.currentThemePage().subscribe(page => {
			this.currentThemePage = page;			
			if(this.themes == null){
				this.themes = [];
			}
			page.items.forEach(item => this.themes.push(item));
		});
		authSvc.currentIdentity.subscribe(identity => 
			identity.doWithValue((i: AuthenticatedIdentity) => 
				i.user.roles.academic.doWithValue((a: Academic) =>
				{
					this.meritAndPromotionHR = a.meritAndPromotionHr;
				})));
	}

	getInstructions(): string {
		return this.tooltipSvc.tooltip("ExploreInstructions." + this.tooltipSvc.mapMeritAndPromotionsHRToString(this.meritAndPromotionHR));
	}

	ngOnDestroy() {
		this.activities = null;
		this.projects = null;
		this.themes = null;
		this.console.log("Explore Whats Happening Component - Destroyed");
	}

	nextPageExists(){
		if(!this.spec) return false;
		switch(this.exploreType){
			case ExploreTypes.Activity:
				if(!this.currentPage) return false;
				return this.currentPage.pageNumber < this.currentPage.totalPageCount;
			case ExploreTypes.Project:
				if(!this.currentProjectPage) return false;
				return this.currentProjectPage.pageNumber < this.currentProjectPage.totalPageCount;
			case ExploreTypes.Theme:
				if(!this.currentThemePage) return false;
				return this.currentThemePage.pageNumber < this.currentThemePage.totalPageCount;
		}
	}

	nextPage(){
		if(!this.spec) return;
		switch(this.exploreType){
			case ExploreTypes.Activity:
				if(!this.currentPage) return;
				this.svc.explore(this.spec, this.currentPage.pageNumber + 1);
				break;
			case ExploreTypes.Project:
				if(!this.currentProjectPage) return;
				this.svc.exploreProject(this.spec, this.currentProjectPage.pageNumber + 1);
				break;
			case ExploreTypes.Theme:
				if(!this.currentThemePage) return;
				this.svc.exploreTheme(this.spec, this.currentThemePage.pageNumber + 1);
				break;
		}
	}

	explore(type: ExploreTypes){
		this.exploreType = type;
		this.exploreWith(this.formValueToSpec());
	}
	private formValueToSpec(): ActivitySpecification {
		return new ActivitySpecification(
			this.form.value.keyword,
			(<SortedSet<Tag>>this.form.value.tags).toArray(),
			this.form.value.showSeparatedAcademics
		);
	}

	private formValueFromSpec(spec: ActivitySpecification, exploreType: ExploreTypes): void {
		this.form.setValue({
			keyword: spec.matchingKeyword,
			tags: new SortedSet(spec.havingAtLeastOneTagOf),
			showSeparatedAcademics: spec.showSeparatedAcademics
		});
		this.exploreType = exploreType;
	}

  	saveCurrentSearch(){
    	let name = prompt("Please name your search");
    	let trimmedName = (name || '').trim();
    	if(trimmedName.length == 0){
      		return;
    	}
    	var data = new SavedSearchDto(trimmedName, this.formValueToSpec());

		this.svc.saveSearch(data).then(result => 
		result.matchDo(
			savedSearch => {
				this.snackBar.open("Search saved", null, {duration:10000});
			},
			errorMessage => {
				this.snackBar.open("Search not saved: " + errorMessage, null, {duration:10000});
			}));
	}

	exploreWith(spec: ActivitySpecification, ss: SavedSearch = null){
		this.spec = spec;
		switch(this.exploreType){
			case ExploreTypes.Activity:
				this.activities = null;
				this.currentPage = null;
				this.formValueFromSpec(this.spec, ExploreTypes.Activity);
				if(ss)
					this.svc.exploreSavedSearch(ss.id, 1);
				else
					this.svc.explore(this.spec, 1);
				break;
			case ExploreTypes.Project:
				this.projects = null;
				this.currentProjectPage = null;
				this.formValueFromSpec(spec, ExploreTypes.Project);
				if(ss)
					this.svc.exploreSavedSearchProject(ss.id, 1);				
				else
					this.svc.exploreProject(this.spec, 1);
				break;
			case ExploreTypes.Theme:
				this.themes = null;
				this.currentThemePage = null;
				this.formValueFromSpec(spec, ExploreTypes.Theme);
				if(ss)
					this.svc.exploreSavedSearchTheme(ss.id, 1);
				else
					this.svc.exploreTheme(this.spec, 1);
				break;
		}
	}
	toHTML(input) : any {
		return new DOMParser().parseFromString(input, "text/html").documentElement.textContent;
	}
	public exportToExcel(){
		let startdate = null;
		let endDate = null;
		let activityList = [];
		if(this.downloadActivities && this.downloadActivities.length >0){
          activityList = this.downloadActivities.map(x=> {
			x.date.matchDo(
				single => {
					startdate = single.date;
					endDate = single.date;
				},
				range => {
					startdate = range.startDate;
					endDate = range.endDate;
				},
				ongoing => {
					endDate = ongoing.startDate;
				});
           const activityObj:any ={
             ActivityOwnerName: x.owner?.name,
			 Email: x.owner?.email,
			 AcademicTitle : x.owner?.title,
			 ActivityName: x.name,
			 ActivityType: x.type,
			 ActivitySubType: x.subType,
			 Role: x.role,
			 Topic: x.topic,
			 Location: x.location,
			 Startdate:	startdate,
			 EndDate:endDate,
			 ActivitylastUpdatedDate: x.activitylastUpdatedDate,
			 ActivityDateType: x.activityDateType
		   }
			return activityObj;
		});
	  }
        let fileName = `ExploreReport.xlsx`;
		const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(activityList);
		const wb: XLSX.WorkBook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Activity Report');
		const projectList = this.exportProjectToExcel();
		const wsProject: XLSX.WorkSheet = XLSX.utils.json_to_sheet(projectList);
		XLSX.utils.book_append_sheet(wb, wsProject, 'Project Report');
		const themeList = this.exportThemeToExcel();
		const wsTheme: XLSX.WorkSheet = XLSX.utils.json_to_sheet(themeList);
		XLSX.utils.book_append_sheet(wb, wsTheme, 'Theme Report');

		XLSX.writeFile(wb, fileName);
	}
	public exportProjectToExcel(){
	  
	  	const MAX_CELL_LENGTH = 32767; // Maximum characters allowed in an Excel cell

	  	let projectList = [];

	  	if(this.downloadProjects && this.downloadProjects.length > 0){
         
			projectList = this.downloadProjects.map(x=> {

				const trimmedDescription = this.toHTML(x.description).length > MAX_CELL_LENGTH
				? this.toHTML(x.description).substring(0, MAX_CELL_LENGTH - 3) + '...' // Trim and add ellipsis (PROJB-421)
				: this.toHTML(x.description);

				const projectObj:any ={
					ProjectOwnerName: x.owner?.name,
					Email: x.owner?.email,
					AcademicTitle : x.owner?.title,
					ProjectName: x.name,
					Description: trimmedDescription,
					Role: x.role,
					Startdate:x.startDate,
					ProjectlastUpdatedDate: x.projectlastUpdatedDate
				}
					
				return projectObj;
			});
	  	}
			  
	  	return projectList;
	}
	public exportThemeToExcel(){
	  let themeList = [];
      if(this.downloadThemes && this.downloadThemes.length>0){
          themeList = this.downloadThemes.map(x=> {
           const themeObj:any ={
             ThemeOwnerName: x.owner?.name,
			 Email: x.owner?.email,
			 AcademicTitle : x.owner?.title,
			 ThemeName: x.name,
			 ThemelastUpdatedDate: x.themelastUpdatedDate
		   }
			return themeObj;
		});
	  }
	  return themeList;
	}
	public downloadReport(){
		this.downloadActivities = [];
		this.downloadProjects = [];
		this.downloadThemes = [];
		this.loading =true;
		this.svc.downloadExploreReport(this.formValueToSpec()).subscribe(response => {
		  this.loading =false;

		  let activities = Page.fromJson(response[0], Activity.fromJson);
		  activities.items.forEach(item => {
				this.downloadActivities.push(item)
		  });

		  let projects = Page.fromJson(response[1], Project.fromJson);
		  projects.items.forEach(item => {
				this.downloadProjects.push(item)
		  });

		  let themes = Page.fromJson(response[2], Theme.fromJson);
		  themes.items.forEach(item => {
				this.downloadThemes.push(item)
		  });

		  this.exportToExcel();
		},err =>{
			this.loading =false;
			this.snackBar.open("Download Failed", null, {duration:10000});
		});
	}

	isSeparatedAcademic(academicOwner: AcademicOwner){
		return academicOwner.separationDate && academicOwner.separationDate < this.today;
	}

	getPageTotalItemCount(){
		switch(this.exploreType) {
			case ExploreTypes.Activity: return (!this.currentPage || !this.currentPage.totalItemCount) ? 0 : this.currentPage.totalItemCount; 
			case ExploreTypes.Project: return (!this.currentProjectPage || !this.currentProjectPage.totalItemCount) ? 0 : this.currentProjectPage.totalItemCount;
			case ExploreTypes.Theme: return (!this.currentThemePage || !this.currentThemePage.totalItemCount) ? 0 : this.currentThemePage.totalItemCount;
		}
	}

	filterExploreData(){
		let filterable: (Activity | Project | Theme)[] | null;

		switch(this.exploreType) {
			case ExploreTypes.Activity: filterable = this.activities; break;
			case ExploreTypes.Project:  filterable = this.projects;   break;
			case ExploreTypes.Theme:    filterable = this.themes;     break;
		}

		return !filterable ? [] : filterable; 
	}
}